"use client"

import Text from "@/components/common/Typography/Text"
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Col, Row, Table, TableColumnProps, theme } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import Title from "../Typography/Title"

import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { getVietnamHolidayMap, getVietnamHolidaysInMonth } from "@/utils/app/getVietnamHolidays"
import { useTranslation } from "react-i18next"

// dayjs-lunar kéo theo lunar-typescript (~97KB). Tải ĐỘNG, chỉ khi Calendars
// mount (lúc mở lịch), để không nằm trong bundle JS đầu tiên.
let lunarExtended = false
let lunarLoadingPromise: Promise<void> | null = null
const ensureLunar = () => {
  if (lunarExtended) return Promise.resolve()
  if (!lunarLoadingPromise) {
    lunarLoadingPromise = import('dayjs-lunar').then((m) => {
      dayjs.extend(m.default)
      lunarExtended = true
    })
  }
  return lunarLoadingPromise
}

const daysOfWeek = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const
const flightDayTitles: Record<(typeof daysOfWeek)[number], string> = {
  T2: 'Thứ 2',
  T3: 'Thứ 3',
  T4: 'Thứ 4',
  T5: 'Thứ 5',
  T6: 'Thứ 6',
  T7: 'Thứ 7',
  CN: 'CN',
}

const formatFlightTooltipDate = (d: Dayjs) => d.format('D [thg] M')

const generateDatesSource = (date: Dayjs) => {
  const firstDate = date.startOf('month')
  const lastDate = date.endOf('month')
  const monthKey = date.format('YYYY-MM')

  const firstDdd = firstDate.format('ddd')
  const lastDdd = lastDate.format('ddd')

  const maxD = lastDate.get('D')

  let dddFirst = daysOfWeek.indexOf(firstDdd as (typeof daysOfWeek)[number])

  const data: any[] = []

  let row: any = {}
  for (let i = 0; i < dddFirst; i++) {
    const d = daysOfWeek[i];
    row[d] = ""
  }

  for (let i = 1; i <= maxD; i++) {
    row[daysOfWeek[dddFirst]] = i
    if (dddFirst >= daysOfWeek.length - 1) {
      dddFirst = 0
      data.push({ key: `${monthKey}-w${data.length}`, ...row })
      row = {}
    } else {
      dddFirst++
    }
  }

  if (Object.keys(row).length > 0) {
    data.push({ key: `${monthKey}-w${data.length}`, ...row })
  }

  return data
}

export type SelectedDates = [Dayjs | string | null | undefined, Dayjs | string | null | undefined]

export type CalendarsProps = {
  range?: boolean,
  onChange?: (value: SelectedDates) => void,
  type?: 'drawer' | 'popup',
  variant?: 'default' | 'flight',
  numberOfMonths?: number,
  value?: [Dayjs, Dayjs],
  selector?: 'from' | 'to',
  getRefContent?: (current?: any) => void,
  minRangeLen?: number,
  subData?: any,
  onCalendarChange?: (dateStr: string) => void,
  onDoubleClick?: (e?: any) => void
}

export type CalendarsRef = {
  next: () => void,
  prev: () => void,
  focus: () => void,
}

type CalendarsInternalProps = {
  close?: () => void,
  onFocus?: () => void
  onBlur?: () => void
}

const formatDate = 'YYYY-MM-DD'

const parseCalendarPrice = (label?: string) => {
  if (!label) return null
  const n = Number(String(label).replace(/[^\d]/g, ''))
  return Number.isFinite(n) && n > 0 ? n : null
}

const Calendars = forwardRef<CalendarsRef, CalendarsProps & CalendarsInternalProps>(
  ({ onDoubleClick, numberOfMonths = 2, value, close, onFocus, getRefContent, onBlur, range, selector, onChange, onCalendarChange, type = 'popup', variant = 'default', minRangeLen = 0, subData }, ref) => {
    const [rangeDates, setRangeDates] = useState([dayjs(), dayjs().add(numberOfMonths - 1, 'month')])
    const refContainer = useRef<HTMLDivElement>(null)
    const isDrawer = type === 'drawer'
    const [selectedDates, setSelectedDate] = useState<SelectedDates>([null, null])
    const [hoverDate, setHoverDate] = useState<string | null>(null)
    const isDesktop = useIsDesktop()
    const now = useMemo(() => dayjs(), [])
    const token = theme.useToken().token
    const { getValidDayjs } = useTETranslation()
    const { t } = useTranslation()
    const cacheCallChangesRef = useRef<Record<string, boolean>>({})
    const cacheStartDateRef = useRef<string | null>(null)
    const timeoutCallChangesRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Nạp plugin âm lịch động; khi xong thì re-render để hiện số ngày âm lịch.
    const [lunarReady, setLunarReady] = useState(lunarExtended)
    useEffect(() => {
      if (lunarReady) return
      let mounted = true
      ensureLunar().then(() => { if (mounted) setLunarReady(true) })
      return () => { mounted = false }
    }, [lunarReady])

    // While picking the return / end date, days before departure must stay disabled.
    const minEndDate = useMemo(() => {
      if (!range) return null
      const startStr = selectedDates[0]
        ? String(selectedDates[0])
        : selector === 'to'
          ? (cacheStartDateRef.current || (value?.[0] ? getValidDayjs(value[0]).format(formatDate) : null))
          : null
      const isPickingEnd = Boolean(
        startStr && (selector === 'to' || (variant === 'flight' && selectedDates[0] && !selectedDates[1]))
      )
      if (!isPickingEnd || !startStr) return null
      return getValidDayjs(startStr)
    }, [range, selectedDates, selector, variant, value, getValidDayjs])

    const visibleMonths = useMemo(
      () => Array.from({ length: numberOfMonths }, (_, index) => rangeDates[0].add(index, 'month')),
      [rangeDates, numberOfMonths],
    )

    const holidayByDate = useMemo(
      () => (variant === 'flight' ? getVietnamHolidayMap(visibleMonths) : {}),
      [variant, visibleMonths],
    )

    const lowestPriceValue = useMemo(() => {
      if (variant !== 'flight' || !subData) return null
      let lowest: number | null = null
      visibleMonths.forEach((month) => {
        const start = month.startOf('month')
        const daysInMonth = start.daysInMonth()
        for (let day = 1; day <= daysInMonth; day++) {
          const date = start.date(day)
          if (date.diff(now, 'days') < 0) continue
          const price = parseCalendarPrice(subData[date.format(formatDate)])
          if (price != null && (lowest === null || price < lowest)) {
            lowest = price
          }
        }
      })
      return lowest
    }, [variant, subData, visibleMonths, now])

    const visibleHolidays = useMemo(
      () => (variant === 'flight' ? visibleMonths.flatMap((month) => getVietnamHolidaysInMonth(month)) : []),
      [variant, visibleMonths],
    )

    /**
     * Compute the visual state of a calendar cell.
     * Handles: selected range, hover preview (desktop only).
     */
    const getCellState = (date: Dayjs) => {
      const dateString = date.format(formatDate)
      const startStr = selectedDates[0] ? String(selectedDates[0]) : null
      const endStr = selectedDates[1] ? String(selectedDates[1]) : null

      let isStartDate = dateString === startStr
      const isEndDate = range && dateString === endStr

      // Regular in-range (when both dates are selected)
      let isInRange = false
      if (range && startStr && endStr) {
        const startVal = getValidDayjs(startStr).valueOf()
        const endVal = getValidDayjs(endStr).valueOf()
        const dateVal = date.valueOf()
        isInRange = dateVal > startVal && dateVal < endVal && !isStartDate && !isEndDate
      }

      // Hover preview (desktop): when start is set — also after both dates are chosen (flight tooltip)
      let isHoverInRange = false
      let isHoverEnd = false
      let isHoverStart = false
      let isHoverOutsideRange = false
      let isHoverInsideRange = false
      let isExtendAfterEnd = false

      if (range && startStr && hoverDate && isDesktop) {
        const startVal = getValidDayjs(startStr).valueOf()
        const hoverVal = getValidDayjs(hoverDate).valueOf()
        const dateVal = date.valueOf()
        const endVal = endStr ? getValidDayjs(endStr).valueOf() : null
        const onlyStartSelected = !endStr

        if (hoverVal > startVal) {
          if (dateString === hoverDate) {
            if (endVal != null && hoverVal < endVal) {
              // Hình 5 — hover a day inside the selected range
              isHoverInsideRange = true
            } else if (endVal != null && hoverVal === endVal) {
              isHoverInsideRange = true
            } else {
              // Hình 4 — hover outside (or only start selected)
              isHoverEnd = true
              isHoverOutsideRange = true
            }
          }

          if (endVal != null && hoverVal > endVal) {
            if (isEndDate) {
              isExtendAfterEnd = true
            }
            if (dateVal > endVal && dateVal < hoverVal) {
              isHoverInRange = true
            }
          } else if (onlyStartSelected) {
            if (dateVal > startVal && dateVal < hoverVal) {
              isHoverInRange = true
            }
          }
        } else if (hoverVal < startVal && !minEndDate) {
          if (dateString === hoverDate) {
            isHoverStart = true
            isHoverOutsideRange = true
          }
          if (onlyStartSelected && dateString === startStr) {
            isStartDate = false
            isHoverEnd = true
          }
          if (onlyStartSelected && dateVal > hoverVal && dateVal < startVal) {
            isHoverInRange = true
          }
        }
      }

      return {
        isInRange,
        isEndDate,
        isStartDate,
        isHoverInRange,
        isHoverEnd,
        isHoverStart,
        isHoverOutsideRange,
        isHoverInsideRange,
        isExtendAfterEnd,
      }
    }

    const resolveStartDateStr = () => {
      if (selectedDates[0]) return String(selectedDates[0])
      if (selector === 'to') {
        if (cacheStartDateRef.current) return cacheStartDateRef.current
        if (value?.[0]) return getValidDayjs(value[0]).format(formatDate)
      }
      return null
    }

    /**
     * Handle cell click with improved range selection logic:
     * 
     * Single mode: select date → close.
     * 
     * Range mode (no selector):
     *   - If 0 or 2 dates selected → set as START, clear END.
     *   - If only START selected:
     *     - Click >= START → set as END, complete.
     *     - Click < START → SWAP (click=START, old START=END), complete.
     * 
     * Range mode (selector='from'):
     *   - Set as START, clear END, call onChange immediately.
     *   - Next click follows the "only START selected" logic above.
     * 
     * Range mode (selector='to' or flight return):
     *   - Days before START are disabled.
     *   - If click >= START → set as END, complete.
     *   - If click < START → ignore.
     */
    const onCellClick = (newValue: Dayjs | null | undefined) => {
      if (!newValue) return

      const newDateStr = newValue.format(formatDate)
      let nextStart: string | null = null
      let nextEnd: string | null = null
      let shouldClose = false
      let shouldCallOnChange = false

      if (!range) {
        // ── Single date mode ──
        nextStart = newDateStr
        shouldClose = true
        shouldCallOnChange = true
      } else {
        // ── Range mode ──
        const startStr = selectedDates[0] ? String(selectedDates[0]) : null
        const endStr = selectedDates[1] ? String(selectedDates[1]) : null
        const selectedCount = [startStr, endStr].filter(Boolean).length

        // Determine if this click is "selecting start" or "selecting end"
        // Selecting start when: nothing selected OR both selected (re-start), AND not explicitly picking 'to'
        const isSelectingStart =
          (selectedCount === 0 || selectedCount === 2) &&
          (selector === 'from' || !selector)

        if (isSelectingStart) {
          // ── Selecting START date ──
          nextStart = newDateStr
          nextEnd = null

          // For selector='from', notify parent immediately (start changed, end cleared)
          if (selector === 'from') {
            shouldCallOnChange = true
          }
          // Don't close - wait for end date selection
        } else {
          // ── Selecting END date ──
          const effectiveStartStr = resolveStartDateStr()

          if (!effectiveStartStr) {
            // No start date available → treat as start
            nextStart = newDateStr
            nextEnd = null
          } else {
            const startDate = getValidDayjs(effectiveStartStr)
            const lockEndAfterStart = selector === 'to' || variant === 'flight'

            if (!newValue.isBefore(startDate, 'day')) {
              // ✓ END >= START → valid selection
              nextStart = effectiveStartStr
              nextEnd = newDateStr
              shouldClose = true
              shouldCallOnChange = true
            } else if (lockEndAfterStart) {
              // Return/end date cannot be before departure
              return
            } else {
              // Natural range: SWAP values for valid data
              nextStart = newDateStr
              nextEnd = effectiveStartStr
              shouldClose = true
              shouldCallOnChange = true
            }
          }
        }
      }

      // Apply minimum range length constraint
      if (minRangeLen && nextStart && nextEnd) {
        const s = getValidDayjs(nextStart)
        const e = getValidDayjs(nextEnd)
        if (e.diff(s, 'day') < minRangeLen) {
          nextEnd = s.add(minRangeLen, 'day').format(formatDate)
        }
      }

      const nextValue: SelectedDates = [nextStart, nextEnd]
      setSelectedDate(nextValue)
      setHoverDate(null)

      // Update cache for cross-session start date persistence
      if (selector !== 'to') {
        cacheStartDateRef.current = nextStart
      }

      if (shouldCallOnChange && onChange) {
        onChange([nextStart, !range ? null : nextEnd])
      }

      if (shouldClose && close) {
        close()
      }
    }

    const renderCalendar = (date: Dayjs) => {
      const source = generateDatesSource(date)
      const renderCell: TableColumnProps['render'] = (value, record, index) => {
        if (!value) {
          return <></>
        }
        const d = date.set('date', value)

        const disabled = d.diff(now, 'days') < 0 || Boolean(minEndDate && d.isBefore(minEndDate, 'day'))

        const dString = d.format(formatDate)
        const lunarDate = lunarReady ? (d as any).lunar('DD') : '';

        const { isEndDate, isInRange, isStartDate, isHoverInRange, isHoverEnd, isHoverStart, isHoverOutsideRange, isHoverInsideRange, isExtendAfterEnd } = getCellState(d)

        const current = dString === dayjs().format(formatDate)
        const isFlight = variant === 'flight'
        const isSunday = d.day() === 0
        const isHoliday = Boolean(holidayByDate[dString])
        const priceLabel = subData?.[dString]
        const cellPrice = parseCalendarPrice(priceLabel)
        const isLowestPrice = Boolean(
          isFlight &&
          !disabled &&
          lowestPriceValue != null &&
          cellPrice != null &&
          cellPrice === lowestPriceValue,
        )
        const startStr = selectedDates[0] ? String(selectedDates[0]) : null
        const endStr = selectedDates[1] ? String(selectedDates[1]) : null
        const hasRangeExtension = Boolean(
          isStartDate && (endStr || (hoverDate && getValidDayjs(hoverDate).valueOf() > d.valueOf()))
        )
        const showFlightTooltip = Boolean(
          isFlight &&
          isDesktop &&
          !disabled &&
          startStr &&
          hoverDate === dString &&
          hoverDate !== startStr
        )
        let tooltipText = ''
        if (showFlightTooltip && startStr && hoverDate) {
          const startD = getValidDayjs(startStr)
          const hoverD = getValidDayjs(hoverDate)
          if (range) {
            const depart = hoverD.valueOf() < startD.valueOf() ? hoverD : startD
            const ret = hoverD.valueOf() < startD.valueOf() ? startD : hoverD
            tooltipText = `Khởi hành vào ${formatFlightTooltipDate(depart)} | Quay về vào ${formatFlightTooltipDate(ret)}`
          } else {
            tooltipText = `Khởi hành vào ${formatFlightTooltipDate(hoverD)}`
          }
        }

        // Build className declaratively (no DOM manipulation needed)
        const cellClasses = [
          'te-date-picker__cell',
          isFlight && 'te-date-picker__cell--flight',
          disabled && 'te-date-picker__cell--disabled',
          !disabled && (isStartDate || isEndDate) && 'te-date-picker__cell--selected',
          isInRange && 'te-date-picker__cell--inrange',
          !disabled && isEndDate && 'te-date-picker__cell--end',
          isStartDate && 'te-date-picker__cell--start',
          hasRangeExtension && 'te-date-picker__cell--has-range',
          isExtendAfterEnd && 'te-date-picker__cell--extend-after',
          isHoverInRange && 'te-date-picker__cell--hover-inrange',
          isHoverEnd && 'te-date-picker__cell--hover-end',
          isHoverStart && 'te-date-picker__cell--hover-start',
          isHoverOutsideRange && 'te-date-picker__cell--hover-outside',
          isHoverInsideRange && 'te-date-picker__cell--hover-inside',
          current && isFlight && 'te-date-picker__cell--today',
          isSunday && isFlight && 'te-date-picker__cell--sunday',
          isHoliday && isFlight && 'te-date-picker__cell--holiday',
        ].filter(Boolean).join(' ')

        if (isFlight) {
          return (
            <Row
              key={d.format('DD/MM')}
              onFocus={onFocus}
              title={showFlightTooltip ? undefined : dString}
              onClick={() => disabled ? null : onCellClick(d)}
              className={cellClasses}
              justify='center'
              align='middle'
              onMouseEnter={isDesktop ? () => setHoverDate(disabled ? null : dString) : undefined}
            >
              {isStartDate && !current && <span className='te-date-picker__cell-marker' aria-hidden />}
              {current && (
                <span className='te-date-picker__cell-today-label'>Hôm nay</span>
              )}
              <div className='te-date-picker__cell-content'>
                <span className='te-date-picker__cell-day'>
                  {value}
                  {lunarDate ? (
                    <span className='te-date-picker__cell-lunar'>{lunarDate}</span>
                  ) : null}
                </span>
              </div>
              {subData && priceLabel && (
                <Text
                  className={`te-date-picker__cell-price-text${isLowestPrice ? ' te-date-picker__cell-price-text--lowest' : ''}`}
                >
                  {priceLabel}
                </Text>
              )}
              {showFlightTooltip && (
                <div className='te-date-picker__cell-tooltip' role='tooltip'>
                  <span className='te-date-picker__cell-tooltip-text'>{tooltipText}</span>
                  <span className='te-date-picker__cell-tooltip-arrow' aria-hidden />
                </div>
              )}
            </Row>
          )
        }

        return (
          <Row
            key={d.format('DD/MM')}
            onFocus={onFocus}
            title={dString}
            onClick={(e) => disabled ? null : onCellClick(d)}
            className={cellClasses}
            justify='center'
            align='middle'
            onMouseEnter={isDesktop ? () => setHoverDate(disabled ? null : dString) : undefined}
          >
            <Row
              justify='center'
              align='middle'
              style={{ textAlign: 'center', width: '100%', height: "100%", position: 'absolute', top: 0, left: 0 }}
            >
              <span className='te-date-picker__cell-day'>
                {value}
                {lunarDate ? (
                  <span className='te-date-picker__cell-lunar'>{lunarDate}</span>
                ) : null}
              </span>
            </Row>
            {subData && (
              <Row
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%'
                }}
                justify='center'
              >
                <Text strong={current} type={current ? 'success' : undefined} style={{ fontSize: '1rem', textAlign: 'center', ...(!current ? { color: !isStartDate && !isEndDate ? token.colorText : token.colorPrimaryBorder } : {}) }}>
                  {subData[dString]}
                </Text>
              </Row>
            )}
          </Row>
        )
      }
      return (
        <Table
          className={`te-date-picker__table--${type} te-date-picker__table${range ? " te-date-picker__table--range" : ""}${selectedDates.some(d => !!d) ? " te-date-picker__table--selected" : ""}`}
          bordered={false}
          pagination={false}
          rowKey="key"
          dataSource={source}
          columns={daysOfWeek.map((i) => ({
            dataIndex: i,
            key: `${i}_${date.format('MM_YYYY')}`,
            title: variant === 'flight' ? flightDayTitles[i] : i,
            render: renderCell,
          }))}
        />
      )
    }

    const gotoNext = () => {
      setRangeDates(([start, end]) => {
        const [nextStart, nextEnd] = [start.add(numberOfMonths - 1, 'month'), end.add(numberOfMonths - 1, 'month')]
        return [nextStart, nextEnd]
      })
    }

    const gotoPrev = () => {
      setRangeDates(([start, end]) => {
        const [nextStart, nextEnd] = [start.add(numberOfMonths * (-1), 'month'), end.add(numberOfMonths * (-1), 'month')]
        return [nextStart, nextEnd]
      })
    }

    useImperativeHandle(ref, () => {
      return {
        prev: gotoPrev,
        next: gotoNext,
        focus: () => refContainer.current?.focus()
      }
    }, [refContainer.current])

    useEffect(() => {
      if (value?.[0]) {
        cacheStartDateRef.current = getValidDayjs(value[0]).format(formatDate)
      }
      if (value && selectedDates.every(d => !d)) {
        const values = (value as any).map((v: any) => v.format(formatDate))
        setSelectedDate(values)
        setTimeout(() => {
          setRangeDates([getValidDayjs(value[0]), getValidDayjs(value[0]).add(numberOfMonths - 1, 'month')])
        }, 200)
      }
    }, [value, selectedDates, numberOfMonths, getValidDayjs])

    useEffect(() => {
      if (rangeDates[0]) {
        const dateString = rangeDates[0].format(formatDate)
        if (!!cacheCallChangesRef.current[dateString]) {
          return
        }
        cacheCallChangesRef.current[dateString] = true
        if (timeoutCallChangesRef.current) {
          clearTimeout(timeoutCallChangesRef.current)
        }
        timeoutCallChangesRef.current = setTimeout(() => {
          onCalendarChange && onCalendarChange(dateString)
        }, 500)

      }
    }, [rangeDates])

    useEffect(() => {
      return () => {
        if (timeoutCallChangesRef.current) {
          clearTimeout(timeoutCallChangesRef.current)
        }
      }
    }, [])

    return (
      <Row
        className={`te-date-picker${variant === 'flight' ? ' te-date-picker--flight' : ''}`}
        onFocus={onFocus}
        onBlur={onBlur}
        ref={(current) => {
          refContainer.current = current
          getRefContent && getRefContent(current)
        }}
        style={isDrawer ? { minWidth: '100%' } : { minWidth: 'unset' }}
        onMouseLeave={isDesktop ? () => setHoverDate(null) : undefined}
      >
        <Col span={24}>
          <Row justify='center' gutter={[20, 20]}>
            {Array(numberOfMonths).fill(null).map((_, index) => {
              const first = index === 0
              const last = index === numberOfMonths - 1

              const date = rangeDates[0].add(index, 'month')
              return (
                <Col
                  key={date.format('YYYY-MM')}
                  flex={isDrawer ? (variant === 'flight' ? '100%' : '40rem') : undefined}
                  span={isDrawer ? undefined : 12}
                  style={isDrawer && variant === 'flight' ? { maxWidth: '100%', width: '100%' } : undefined}
                >
                  {/* START: HEADER */}
                  <Row justify='space-between' wrap={false}>
                    {!isDrawer && first && (
                      <Col>
                        <Button onClick={() => gotoPrev()} type='text' icon={<FontAwesomeIcon icon={faChevronLeft} />} />
                      </Col>
                    )}
                    <Col flex={1}>
                      <Row justify='center'>
                        <Title level={4} style={{ margin: 0 }}>
                          {variant === 'flight'
                            ? date.format('[tháng] M [năm] YYYY')
                            : date.format('MMMM YYYY')}
                        </Title>
                      </Row>
                    </Col>
                    {!isDrawer && last && (
                      <Col>
                        <Button onClick={() => gotoNext()} type='text' icon={<FontAwesomeIcon icon={faChevronRight} />} />
                      </Col>
                    )}
                  </Row>
                  {/* END: HEADER */}
                  <Row justify='center'>
                    {renderCalendar(date)}
                  </Row>
                </Col>
              )
            })}
          </Row>
          {variant === 'flight' && (lowestPriceValue != null || visibleHolidays.length > 0) && (
            <div className="te-date-picker__footer">
              {visibleHolidays.length > 0 && (
                <div className="te-date-picker__holidays">
                  {visibleHolidays.map((item, holidayIndex) => (
                    <span
                      key={item.date}
                      className={`te-date-picker__holiday te-date-picker__holiday--${(holidayIndex % 4) + 1}`}
                    >
                      <b className="te-date-picker__holiday-day">{dayjs(item.date).date()}</b>
                      <span className="te-date-picker__holiday-name">{t(`flight.${item.nameKey}`)}</span>
                    </span>
                  ))}
                </div>
              )}
              {lowestPriceValue != null && (
                <div className="te-date-picker__legend">
                  <span className="te-date-picker__legend-lowest">
                    <span className="te-date-picker__legend-dot" aria-hidden />
                    {t("flight.lowest_flight_price")}
                  </span>
                </div>
              )}
            </div>
          )}

        </Col>
      </Row>
    )
  })

export default Calendars